import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
const pageVariants = {
  initial: { opacity: 0, scale: 0.96, filter: "blur(6px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, x: -60, filter: "blur(4px)" },
};
const ideas = [
  {
    icon: "🔬",
    title: "Educational Subject-Based Murals",
    desc: "Science-themed walls can showcase the solar system, human anatomy, or famous inventions. Mathematics walls can feature formulas, geometric shapes, and number games. These murals help students learn even outside the classroom.",
  },
  {
    icon: "💡",
    title: "Inspirational Quotes & Values",
    desc: "Walls featuring motivational quotes, leadership messages, and core values can positively influence students. Inspirational wall art promotes confidence, discipline, teamwork, and personal growth.",
  },
  {
    icon: "🗺️",
    title: "Interactive Learning Walls",
    desc: "World maps, language charts, historical timelines, and educational puzzles encourage students to engage with content visually and actively.",
  },
  {
    icon: "🌿",
    title: "Nature & Environmental Themes",
    desc: "Nature-inspired paintings featuring trees, wildlife, oceans, and sustainability messages help students develop environmental awareness while creating a calming atmosphere.",
  },
  {
    icon: "🎪",
    title: "Creative Play Area Murals",
    desc: "For kindergarten and primary schools, colorful murals featuring cartoons, alphabets, animals, and storytelling characters make learning fun and support early childhood development.",
  },
];
function Blog2() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.7, ease: "easeInOut" }}
      style={{ background: "var(--canvas)", minHeight: "100vh" }}
    >
      <div
        style={{
          background:
            "linear-gradient(150deg,#0a1828 0%,#0f2a1a 60%,#0f0520 100%)",
          padding: "100px 6% 70px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "60% 40% 55% 45% / 50% 60% 40% 55%",
            background: "var(--teal)",
            opacity: 0.1,
            top: -60,
            right: -60,
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "var(--lavender)",
            opacity: 0.07,
            bottom: -30,
            left: 80,
          }}
        />
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            color: "#c9982a",
            fontWeight: 700,
            fontSize: "0.78rem",
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginBottom: "16px",
          }}
        >
          EDUCATIONAL SPACES · SCHOOL ART
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.8 }}
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 900,
            fontSize: "clamp(1.8rem,5vw,3.2rem)",
            color: "#fff",
            lineHeight: 1.2,
            maxWidth: 720,
            margin: "0 auto 24px",
          }}
        >
          School Wall Painting Ideas That Inspire Learning
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={{
            color: "rgba(255,255,255,0.55)",
            fontSize: "0.88rem",
            letterSpacing: "1px",
          }}
        >
          6 min read
        </motion.p>
      </div>
      <div
        style={{
          maxWidth: 780,
          margin: "0 auto",
          padding: "64px 6% 100px",
        }}
      >
        <p
          style={{
            fontSize: "1.1rem",
            lineHeight: 1.85,
            color: "#333",
            marginBottom: "32px",
            borderLeft: "4px solid #0ab5a8",
            paddingLeft: "20px",
            fontStyle: "italic",
          }}
        >
          A school's environment plays a vital role in shaping a student's
          learning experience. Beyond classrooms and textbooks, visually
          engaging spaces can spark creativity, improve focus, and make learning
          more enjoyable.
        </p>
        <h2
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "1.7rem",
            fontWeight: 700,
            color: "#1a0a2e",
            margin: "48px 0 16px",
          }}
        >
          Why School Wall Paintings Matter
        </h2>
        <p style={{ lineHeight: 1.85, color: "#444", marginBottom: "40px" }}>
          Children spend a significant part of their day at school. Bright,
          creative, and educational wall paintings can transform plain walls
          into interactive learning tools. Well-designed school wall murals not
          only beautify the campus but also encourage curiosity, imagination,
          and knowledge retention.
        </p>
        <h2
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "1.7rem",
            fontWeight: 700,
            color: "#1a0a2e",
            margin: "48px 0 28px",
          }}
        >
          5 School Wall Painting Ideas
        </h2>
        <div style={{ display: "grid", gap: "20px" }}>
          {ideas.map((idea, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              style={{
                display: "flex",
                gap: "20px",
                padding: "24px",
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.05)",
                border: "1px solid #f0ece4",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  background: "rgba(10,181,168,0.1)",
                  border: "1.5px solid rgba(10,181,168,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.4rem",
                  flexShrink: 0,
                }}
              >
                {idea.icon}
              </div>
              <div>
                <strong
                  style={{
                    display: "block",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#1a0a2e",
                    marginBottom: "6px",
                  }}
                >
                  {idea.title}
                </strong>
                <p style={{ lineHeight: 1.75, color: "#555", margin: 0 }}>
                  {idea.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        <h2
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "1.7rem",
            fontWeight: 700,
            color: "#1a0a2e",
            margin: "56px 0 16px",
          }}
        >
          Benefits of Professional School Wall Painting Services
        </h2>
        <p style={{ lineHeight: 1.85, color: "#444", marginBottom: "20px" }}>
          Professional school mural artists can create customized designs that
          align with educational goals, age groups, and available space.
          High-quality murals are durable, visually appealing, and designed to
          inspire students for years.
        </p>
        <h2
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "1.7rem",
            fontWeight: 700,
            color: "#1a0a2e",
            margin: "48px 0 16px",
          }}
        >
          Transform Your School Environment
        </h2>
        <p style={{ lineHeight: 1.85, color: "#444", marginBottom: "44px" }}>
          Investing in school wall painting services is an effective way to
          enhance learning spaces, improve student engagement, and create a
          memorable educational environment. From educational murals to
          interactive learning walls, the right design can turn every corridor
          and classroom into a source of inspiration.
        </p>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <motion.button
              whileHover={{ background: "#b8882a" }}
              style={{
                padding: "13px 28px",
                background: "#c9982a",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                fontWeight: 700,
                fontSize: "0.85rem",
                letterSpacing: "1px",
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "background 0.3s",
              }}
            >
              Get a Quote
            </motion.button>
          </Link>
          <Link to="/services/wall-murals" style={{ textDecoration: "none" }}>
            <motion.button
              whileHover={{ background: "#f0ece4" }}
              style={{
                padding: "13px 28px",
                background: "transparent",
                color: "#1a0a2e",
                border: "1.5px solid #1a0a2e",
                borderRadius: "4px",
                fontWeight: 700,
                fontSize: "0.85rem",
                letterSpacing: "1px",
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "background 0.3s",
              }}
            >
              View Our Work
            </motion.button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
export default Blog2;
